import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, ArrowRight, ArrowLeft, Loader2, Upload, CheckCircle2, PartyPopper } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../lib/api";

const inputCls =
  "w-full bg-white border border-black/15 focus:border-brand focus:ring-2 focus:ring-brand/30 rounded-lg px-5 py-4 text-ink placeholder-black/30 outline-none transition-[border-color,box-shadow] duration-300 font-light";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-black/50 mb-2">{label}</span>
      <input {...props} className={inputCls} />
    </label>
  );
}

const FileField = ({ label, file, onChange, testid, hint }) => (
  <div>
    <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-black/50 mb-2">{label}</span>
    <label className="flex items-center gap-3 border border-dashed border-black/25 hover:border-brand rounded-lg px-5 py-4 cursor-pointer transition-colors duration-300 bg-white">
      <Upload className="w-4 h-4 text-brand shrink-0" />
      <span className="text-sm text-black/60 font-light truncate">{file ? file.name : hint}</span>
      <input data-testid={testid} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => onChange(e.target.files[0] || null)} />
    </label>
  </div>
);

const stepLabels = ["Contact details", "Your business", "Commitment"];

function SignupWizard() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", commitment: false });
  const [brandFile, setBrandFile] = useState(null);
  const [menuFile, setMenuFile] = useState(null);

  const canNext =
    step === 1
      ? form.name.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.phone.trim().length > 5
      : step === 2
      ? form.business_name.trim().length > 1
      : form.commitment;

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("business_name", form.business_name);
      fd.append("commitment", String(form.commitment));
      if (brandFile) fd.append("brand_file", brandFile);
      if (menuFile) fd.append("menu_file", menuFile);
      const res = await axios.post(`${API}/signup`, fd);
      toast.success(res.data.message);
      setDone(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-14" data-testid="signup-success">
        <PartyPopper className="w-10 h-10 text-brand mx-auto" />
        <h3 className="mt-6 font-display font-black uppercase tracking-tight text-2xl text-ink">You're on your way to ownership</h3>
        <p className="mt-4 text-black/60 font-light text-sm max-w-sm mx-auto leading-relaxed">
          We will send your 50% payment link within 24 hours. Work starts right after — your shop launches in under 7 days.
        </p>
      </motion.div>
    );
  }

  if (step === 0) {
    return (
      <div>
        <h3 className="font-display font-black uppercase tracking-tight text-2xl text-ink">Become the owner</h3>
        <p className="mt-3 text-black/60 font-light text-sm leading-relaxed">
          Three quick steps: your contact details, your business assets, and your commitment. We handle everything else.
        </p>
        <ul className="mt-6 space-y-3">
          {["50% to start — payment link sent before we begin", "Remaining 50% only before launch", "Live in under 7 days, 0% commission forever"].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-black/70 font-light">
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>
        <button
          data-testid="signup-start-button"
          onClick={() => setStep(1)}
          className="group mt-8 w-full flex items-center justify-center gap-3 bg-brand hover:bg-black text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-full transition-colors duration-300"
        >
          Start your signup
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-8">
        {stepLabels.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <span
              data-testid={`signup-step-indicator-${i + 1}`}
              className={`w-7 h-7 rounded-full flex items-center justify-center font-mono text-[10px] transition-colors duration-300 ${
                step > i ? "bg-brand text-white" : "bg-black/5 text-black/40"
              }`}
            >
              {i + 1}
            </span>
            <span className={`hidden sm:block font-mono text-[10px] tracking-[0.15em] uppercase ${step > i ? "text-ink" : "text-black/35"}`}>{label}</span>
            {i < stepLabels.length - 1 && <span className="w-6 h-px bg-black/15 mx-1" />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
          {step === 1 && (
            <div className="space-y-5" data-testid="signup-step-1">
              <Field label="Your name" data-testid="signup-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" />
              <Field label="Email" data-testid="signup-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="mario@pizzeria.ch" />
              <Field label="Phone / WhatsApp" data-testid="signup-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41 79 000 00 00" />
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5" data-testid="signup-step-2">
              <Field label="Business name" data-testid="signup-business-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Pizzeria Da Mario" />
              <FileField label="Brand guide / logo / colors" hint="Upload logo or brand guide (image or PDF)" file={brandFile} onChange={setBrandFile} testid="signup-brand-file" />
              <FileField label="Your menu" hint="Upload your menu (PDF or image)" file={menuFile} onChange={setMenuFile} testid="signup-menu-file" />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5" data-testid="signup-step-3">
              <div className="rounded-lg bg-black/[0.03] border border-black/10 p-6">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-brand mb-3">The commitment</div>
                <p className="text-sm text-black/70 font-light leading-relaxed">
                  We will send you a payment link for <span className="font-semibold text-ink">50% before we start the work</span>. The remaining 50% is due only
                  before launch. No subscriptions, no commissions — one price, your system.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="signup-commitment-checkbox"
                  type="checkbox"
                  checked={form.commitment}
                  onChange={(e) => setForm({ ...form, commitment: e.target.checked })}
                  className="w-4 h-4 mt-1 accent-[#D10056]"
                />
                <span className="text-sm text-black/70 font-light">I understand and commit to the 50% / 50% payment terms.</span>
              </label>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex gap-3">
        <button
          data-testid="signup-back-button"
          onClick={() => setStep(step - 1)}
          className="flex items-center gap-2 border border-black/15 text-ink/60 hover:text-ink font-display font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-full transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 ? (
          <button
            data-testid="signup-next-button"
            disabled={!canNext}
            onClick={() => setStep(step + 1)}
            className="group flex-1 flex items-center justify-center gap-2 bg-brand hover:bg-black text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-40"
          >
            Continue <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        ) : (
          <button
            data-testid="signup-submit-button"
            disabled={!canNext || busy}
            onClick={submit}
            className="flex-1 flex items-center justify-center gap-2 bg-brand hover:bg-black text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit — become the owner
          </button>
        )}
      </div>
    </div>
  );
}

export default function CTASection() {
  const [demo, setDemo] = useState({ name: "", email: "", restaurant: "", date: "", time: "" });
  const [busy, setBusy] = useState(false);

  const submitDemo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/demo`, demo);
      toast.success(res.data.message);
      setDemo({ name: "", email: "", restaurant: "", date: "", time: "" });
    } catch (e2) {
      const d = e2.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 border-t border-black/10 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
        >
          Take Ownership
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl text-ink"
        >
          Your regulars are waiting. <span className="text-steel">Bring them home.</span>
        </motion.h2>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <motion.div
            data-testid="signup-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="rounded-xl border border-brand/50 bg-[#FAFAFA] p-10 lg:p-12 card-glow"
          >
            <SignupWizard />
          </motion.div>

          <motion.div
            data-testid="demo-form-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-xl border border-black/10 bg-[#FAFAFA] p-10 lg:p-12"
          >
            <h3 className="font-display font-black uppercase tracking-tight text-2xl text-ink">Book a free demo</h3>
            <p className="mt-3 text-black/60 font-light text-sm">30 minutes. We walk you through the shop, the builder and the admin app — with your menu as the example.</p>
            <form className="mt-8 space-y-5" onSubmit={submitDemo}>
              <Field label="Your name" data-testid="demo-name-input" required value={demo.name} onChange={(e) => setDemo({ ...demo, name: e.target.value })} placeholder="Mario Rossi" />
              <Field label="Email" data-testid="demo-email-input" required type="email" value={demo.email} onChange={(e) => setDemo({ ...demo, email: e.target.value })} placeholder="mario@pizzeria.ch" />
              <Field label="Restaurant / business name" data-testid="demo-restaurant-input" required value={demo.restaurant} onChange={(e) => setDemo({ ...demo, restaurant: e.target.value })} placeholder="Pizzeria Da Mario" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Preferred date" data-testid="demo-date-input" required type="date" value={demo.date} onChange={(e) => setDemo({ ...demo, date: e.target.value })} />
                <Field label="Preferred time" data-testid="demo-time-input" required type="time" value={demo.time} onChange={(e) => setDemo({ ...demo, time: e.target.value })} />
              </div>
              <button
                data-testid="demo-submit-button"
                disabled={busy}
                className="group w-full flex items-center justify-center gap-3 border border-black/25 text-ink hover:bg-black hover:text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-full transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarClock className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-12" />}
                Reserve my demo slot
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
